import React, { useState, useEffect } from 'react';
import styles from './Surveys.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Modal from '../../../components/modal/Modal';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const Surveys = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    start: 1,
    end: 1,
    hasPrev: false,
    hasNext: false
  });

  const fetchSurveys = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10, search };
      const response = await axiosClient.get('/surveys', { params });
      const responseData = response.data;

      setData(responseData.surveys || []);

      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
      showToast(error.message || 'Failed to fetch surveys', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, search]);

  useEffect(() => {
    fetchSurveys(1);
  }, [fetchSurveys]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/surveys/${deletingId}`);
      showToast('Survey deleted successfully');
      setDeletingId(null);
      fetchSurveys(pagination.current);
    } catch (error) {
      showToast(error.message || 'Failed to delete survey', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      header: 'Title',
      key: 'title',
      render: (row) => (
        <span className={styles.titleLink} onClick={() => navigate(`/surveys/edit/${row.id}`)}>
          {row.title}
        </span>
      )
    },
    { header: 'Type', key: 'type' },
    {
      header: 'Submissions',
      key: 'submissionsCount',
      render: (row) => {
        const count = row.submissionsCount ?? 0;
        if (count === 0) return <span>{count} submissions</span>;
        return (
          <span
            className={styles.countLink}
            onClick={() => navigate(`/surveys/submissions/${row.id}`)}
          >
            {count} submissions
          </span>
        );
      },
    },
    {
      header: '',
      key: 'actions',
      render: (row) => (
        <button className={styles.deleteBtn} onClick={() => setDeletingId(row.id)}>
          <Icon name="trash" />
        </button>
      ),
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Surveys" />
        <div className={styles.headerActions}>
          <Search onSubmit={(value) => setSearch(value)} />
          <Button
            label={
              <div className={styles.cta}>
                <Icon name="plus" />
                Create Survey
              </div>
            }
            variant="primary"
            onClick={() => navigate('/surveys/create')}
            size="medium"
          />
        </div>
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={(page) => fetchSurveys(page)}
        />
      </div>

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this survey?</h2>
            <div className={styles.confirmButtons}>
              <Button
                label="Delete"
                size="small"
                variant="secondary"
                onClick={handleDelete}
                disabled={isDeleting}
              />
              <Button
                label="Cancel"
                size="small"
                variant="cancel"
                onClick={() => setDeletingId(null)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Surveys;
